import type { AuditEntry } from '@jmc/shared';
import { formatDateTime } from '@jmc/shared';
import { useState } from 'react';
import { useI18n, type MessageKey } from '../lib/i18n.js';
import { cx, EmptyState, Mono, TableSkeleton } from './ui.js';

/**
 * The audit trail, as a table.
 *
 * The log stored the record's state before and after every change from the
 * start, but nothing ever read those two columns back -- so the screen could
 * say that a file was edited without saying what was edited. A supervisor
 * asking "who changed the plot number, and what was it before" had no way to
 * find out from the application.
 *
 * Shared by the Audit screen and by a file's own page, because the two views
 * differ only in which entries they ask the server for.
 */

/** Entity names as stored, mapped to a reader's word for them. */
const ENTITY_LABEL: Record<string, MessageKey> = {
  file_record: 'audit.entity.file_record',
  storage_bag: 'audit.entity.storage_bag',
  file_movement: 'audit.entity.file_movement',
  app_user: 'audit.entity.app_user',
  import_batch: 'audit.entity.import_batch',
};

/**
 * Keys that carry no meaning for a reader and only lengthen the list. The
 * snapshots are the API's own camelCase shape, so these are camelCase too --
 * a snake_case list silently matched nothing.
 */
const HIDDEN_KEYS = new Set([
  'id',
  'updatedAt',
  'createdAt',
  'updatedBy',
  'createdBy',
  'searchVector',
  'isOverdue',
]);

function label(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (character) => character.toUpperCase());
}

function show(value: unknown): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * The fields that actually differ between the two snapshots.
 *
 * Computed here rather than stored, so the trail keeps whole snapshots (which
 * stay readable if a column is later renamed) while the screen still shows a
 * short list.
 */
export function changedFields(
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
): Array<{ key: string; before: unknown; after: unknown }> {
  const keys = new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]);
  const changes: Array<{ key: string; before: unknown; after: unknown }> = [];

  for (const key of keys) {
    if (HIDDEN_KEYS.has(key)) continue;
    const from = before?.[key];
    const to = after?.[key];
    if (JSON.stringify(from ?? null) === JSON.stringify(to ?? null)) continue;
    changes.push({ key, before: from, after: to });
  }

  return changes.sort((a, b) => a.key.localeCompare(b.key));
}

function ChangeList({ entry }: { entry: AuditEntry }) {
  const { t } = useI18n();
  const changes = changedFields(entry.before, entry.after);

  if (changes.length === 0) {
    return <p className="text-[0.8125rem] text-ink-500">{t('audit.noFieldChanges')}</p>;
  }

  return (
    <dl className="grid gap-1.5 text-[0.8125rem]">
      {changes.map((change) => (
        <div key={change.key} className="grid gap-x-3 sm:grid-cols-[10rem_1fr]">
          <dt className="font-medium text-ink-700">{label(change.key)}</dt>
          <dd className="min-w-0">
            <span className="text-ink-400">{t('audit.was')} </span>
            <span className="break-words text-ink-500 line-through">{show(change.before)}</span>
            <span className="text-ink-400"> · {t('audit.now')} </span>
            <span className="break-words font-medium">{show(change.after)}</span>
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function AuditTable({
  entries,
  loading = false,
  showEntity = true,
}: {
  entries: AuditEntry[];
  loading?: boolean;
  showEntity?: boolean;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState<Set<number>>(new Set());

  if (loading) return <TableSkeleton columns={showEntity ? 5 : 4} />;
  if (entries.length === 0) return <EmptyState title={t('common.none')} />;

  const toggle = (id: number) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>{t('audit.when')}</th>
          <th>{t('audit.who')}</th>
          <th>{t('audit.what')}</th>
          {showEntity && <th className="hidden lg:table-cell">{t('audit.entity')}</th>}
          <th>{t('audit.detail')}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry) => {
          const changes = changedFields(entry.before, entry.after);
          const expanded = open.has(entry.id);

          return [
            <tr key={entry.id} className={cx(expanded && 'bg-navy-50/60')}>
              <td className="whitespace-nowrap text-ink-500">{formatDateTime(entry.at)}</td>
              <td className="whitespace-nowrap">{entry.actorName ?? '—'}</td>
              <td>
                <Mono className="rounded bg-paper px-1.5 py-0.5">{entry.action}</Mono>
              </td>
              {showEntity && (
                <td className="hidden whitespace-nowrap lg:table-cell">
                  {ENTITY_LABEL[entry.entity] ? t(ENTITY_LABEL[entry.entity]!) : entry.entity}
                  {entry.entityId && <span className="text-ink-400"> #{entry.entityId}</span>}
                </td>
              )}
              {/* Wraps rather than truncates: a fixed-width cell pushed the
                  table past its container, so the whole trail had to be
                  scrolled sideways to read the first column. */}
              <td className="min-w-[14rem]">
                <div>{entry.summary ?? '—'}</div>
                {changes.length > 0 && (
                  <button
                    type="button"
                    className="mt-0.5 text-[0.75rem] font-medium text-navy-600 hover:underline"
                    aria-expanded={expanded}
                    onClick={() => toggle(entry.id)}
                  >
                    {expanded ? t('audit.hideChanges') : t('audit.showChanges')} (
                    {changes.length})
                  </button>
                )}
              </td>
            </tr>,
            expanded ? (
              <tr key={`${entry.id}-changes`} className="bg-navy-50/60">
                <td colSpan={showEntity ? 5 : 4} className="px-4 py-3">
                  <ChangeList entry={entry} />
                </td>
              </tr>
            ) : null,
          ];
        })}
      </tbody>
    </table>
  );
}
